import { useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Ellipsis } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Checkbox, Icon, MenuItem, MenuList, MenuSeparator, Popover } from '../../../ds/components';
import { ageText, cardValue } from '../format';
import type { BoardCard as BoardCardData, Tone } from '../types';
import styles from './board.module.css';

export const VALUE_MASKED_TEXT = 'Wert nur für Closer sichtbar';

const flagClass: Record<Tone, string | undefined> = {
  warning: styles.flagWarning,
  danger: styles.flagDanger,
  info: styles.flagInfo,
  success: styles.flagSuccess,
  neutral: styles.flagNeutral,
  accent: styles.flagAccent,
};

export interface CardActions {
  onOpen: (card: BoardCardData) => void;
  onChangeStage: (card: BoardCardData) => void;
  onLose: (card: BoardCardData) => void;
  onHistory: (card: BoardCardData) => void;
  onOpenContact: (card: BoardCardData) => void;
  /** Deal-Pipeline 22: the receipt of the commission, offered in the „Provisionseingang" stage. */
  onRecordReceipt: (card: BoardCardData) => void;
}

export interface CardBodyProps {
  card: BoardCardData;
  /** Deal-Pipeline 05: the deadline line replaces the unit line. */
  showDeadline?: boolean | undefined;
}

/** The card's rows (name/age, unit, value/closer, note, flag) — shared by the board card and the drag overlay. */
export function CardBody({ card, showDeadline = false }: CardBodyProps) {
  const overdue = card.overdue;
  return (
    <>
      <div className={styles.cardRow}>
        <span className={styles.cardName}>{card.contactName}</span>
        <span
          className={clsx(
            styles.cardAge,
            overdue && styles.cardAgeOverdue,
            overdue && showDeadline && styles.cardAgeBold,
          )}
        >
          {ageText(card.ageDays)}
        </span>
      </div>
      <div className={styles.cardUnit}>
        {showDeadline && card.deadlineLine ? card.deadlineLine : card.unitLine}
      </div>
      <div className={styles.cardValueRow}>
        <span className={clsx(styles.cardValue, card.valueMasked && styles.cardValueMasked)}>
          {cardValue(card.valueCents, card.valueMasked, VALUE_MASKED_TEXT)}
        </span>
        {card.closerShort ? <span className={styles.cardCloser}>{card.closerShort}</span> : null}
      </div>
      {card.note ? <div className={styles.cardNote}>{card.note}</div> : null}
      {card.referrerPartnerName ? (
        <div
          className={clsx(styles.flag, styles.flagNeutral)}
          title={`Tippgeber · ${card.referrerPartnerName}`}
        >
          Tippgeber
        </div>
      ) : null}
      {card.flag ? (
        <div className={clsx(styles.flag, flagClass[card.flag.tone])}>{card.flag.text}</div>
      ) : null}
    </>
  );
}

export interface BoardCardProps extends CardBodyProps {
  actions: CardActions;
  /** The card can be dragged (server `canDrag` and the board is writable). */
  draggable: boolean;
  /** Read-only board (Deal-Pipeline 12): no menu, no pointer. */
  readOnly: boolean;
  /** Stage actions are offered in the menu (writable board). */
  canChangeStage: boolean;
  cancelled?: boolean | undefined;
  /** The card stands in „Provisionseingang": the menu offers „Provisionseingang erfassen". */
  receiptStage?: boolean | undefined;
  selecting: boolean;
  selected: boolean;
  onToggleSelect: ((card: BoardCardData) => void) | null;
  isDragging: boolean;
  compact?: boolean | undefined;
}

/** A deal card of the pipeline board with its hover checkbox, its menu (Deal-Pipeline 10) and the drag handle. */
export function BoardCard({
  card,
  showDeadline,
  actions,
  draggable,
  readOnly,
  canChangeStage,
  cancelled = false,
  receiptStage = false,
  selecting,
  selected,
  onToggleSelect,
  isDragging,
}: BoardCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: card.id,
    data: { card },
    disabled: !draggable,
  });

  function open(event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();
    actions.onOpen(card);
  }

  /** The menu is portaled: its React events bubble to the card although it lives outside the card's DOM. */
  function insideCard(event: { currentTarget: HTMLDivElement; target: EventTarget }) {
    return event.currentTarget.contains(event.target as Node);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      actions.onOpen(card);
    }
  }

  const dangerBorder = card.flag?.tone === 'danger' && card.flag.text.startsWith('Fina');
  const dragListeners = draggable ? (listeners ?? {}) : {};
  const dragKeyDown = dragListeners['onKeyDown'] as
    ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined;
  const dragPointerDown = dragListeners['onPointerDown'] as
    ((event: PointerEvent<HTMLDivElement>) => void) | undefined;

  return (
    <div
      ref={setNodeRef}
      aria-label={`${card.contactName} · ${card.unitLine}`}
      className={clsx(
        styles.card,
        readOnly && styles.cardStatic,
        dangerBorder && styles.cardDanger,
        cancelled && styles.cardCancelled,
        selected && styles.cardSelected,
        selected && styles.cardChecked,
        isDragging && styles.cardDragging,
      )}
      onClick={(event) => {
        if (!insideCard(event)) return;
        if (selecting && onToggleSelect) {
          onToggleSelect(card);
          return;
        }
        open(event);
      }}
      {...(draggable ? attributes : {})}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (!insideCard(event)) return;
        onKeyDown(event);
        dragKeyDown?.(event);
      }}
      onPointerDown={(event) => {
        if (!insideCard(event)) return;
        dragPointerDown?.(event);
      }}
    >
      {onToggleSelect && !readOnly ? (
        <span
          className={clsx(styles.cardCheck, (selecting || selected) && styles.cardCheckVisible)}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Checkbox
            label={`${card.contactName} auswählen`}
            labelHidden
            checked={selected}
            onChange={() => onToggleSelect(card)}
          />
        </span>
      ) : null}
      <CardBody card={card} showDeadline={showDeadline} />
      {!readOnly ? (
        <>
          <button
            ref={menuRef}
            type="button"
            className={clsx(styles.cardMenu, menuOpen && styles.cardMenuOpen)}
            aria-label={`Kartenmenü ${card.contactName}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((value) => !value);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Icon icon={Ellipsis} size={16} />
          </button>
          <Popover
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorRef={menuRef}
            width={238}
            placement="bottom-end"
            variant="card"
            initialFocus="none"
          >
            <MenuList label="Karte" density="compact" onClose={() => setMenuOpen(false)}>
              <MenuItem
                label="Deal öffnen"
                onSelect={() => {
                  setMenuOpen(false);
                  actions.onOpen(card);
                }}
              />
              {canChangeStage ? (
                <MenuItem
                  label="Stage wechseln …"
                  onSelect={() => {
                    setMenuOpen(false);
                    actions.onChangeStage(card);
                  }}
                />
              ) : null}
              {canChangeStage && receiptStage ? (
                <MenuItem
                  label="Provisionseingang erfassen …"
                  onSelect={() => {
                    setMenuOpen(false);
                    actions.onRecordReceipt(card);
                  }}
                />
              ) : null}
              <MenuItem
                label="Kontakt öffnen"
                onSelect={() => {
                  setMenuOpen(false);
                  actions.onOpenContact(card);
                }}
              />
              <MenuSeparator />
              <MenuItem
                label="Stage-Historie"
                onSelect={() => {
                  setMenuOpen(false);
                  actions.onHistory(card);
                }}
              />
              {canChangeStage && !cancelled ? (
                <MenuItem
                  label="Verloren setzen …"
                  tone="danger"
                  onSelect={() => {
                    setMenuOpen(false);
                    actions.onLose(card);
                  }}
                />
              ) : null}
            </MenuList>
          </Popover>
        </>
      ) : null}
    </div>
  );
}
